import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { fmtDate } from './DcaUtils';

interface LogsPanelProps {
  botId: number;
}

export const LogsPanel: React.FC<LogsPanelProps> = ({ botId }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`/dca/${botId}/logs?page=${p}`);
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch { } finally { setLoading(false); }
  }, [botId]);

  useEffect(() => { fetchLogs(page); }, [page, fetchLogs]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-primary" />
          <span className="text-[11px] font-black text-foreground uppercase tracking-widest">엔진 로그</span>
          <Badge variant="outline" className="bg-primary/5 text-primary border-primary/10 text-[9px] font-bold h-4 px-1">{total.toLocaleString()}건</Badge>
          <span className="text-[10px] text-success font-bold flex items-center gap-1 ml-2">
            <span className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" /> ONLINE
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchLogs(page)}
          disabled={loading}
          className="h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-lg transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-[10px] font-bold">새로고침</span>
        </Button>
      </div>
      <div className="bg-muted/10 rounded-2xl overflow-hidden divide-y divide-border border border-border">
        {logs.length > 0 ? logs.map((log: any) => (
          <div key={log.id} className="p-3 text-[11px] md:text-[12px]">
            <div className="flex justify-between gap-2">
              <span className={`font-normal flex-1 ${log.level === 'error' ? 'text-danger' : (log.level === 'warn' ? 'text-amber-500' : 'text-foreground')}`}>{log.message}</span>
              <div className="flex flex-col items-end gap-1">
                <span className="text-[10px] text-muted-foreground/50 font-medium whitespace-nowrap">{fmtDate(log.created_at)}</span>
                {log.dca_cycle_id && (
                  <span className="text-[8px] px-1.5 py-0.5 bg-primary/5 border border-primary/10 rounded font-black text-primary/60 whitespace-nowrap tracking-tighter">
                    cy #{log.dca_cycle_id}
                  </span>
                )}
              </div>
            </div>
          </div>
        )) : (
          <div className="p-10 text-center text-muted-foreground/30 font-bold">로그가 없습니다.</div>
        )}
      </div>

      {/* 페이지네이션 컨트롤 */}
      {total > 50 && (
        <div className="flex items-center justify-between px-2 py-2 bg-muted/10 rounded-xl">
          <div className="text-[10px] text-muted-foreground font-bold">
            총 {total.toLocaleString()}건 중 {(page - 1) * 50 + 1}~{Math.min(page * 50, total)} 표시
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              className="h-8 w-8 p-0 rounded-lg hover:bg-background border-muted-foreground/20"
              title="첫 페이지"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1 || loading}
              className="h-8 w-8 p-0 rounded-lg hover:bg-background"
              title="이전 페이지"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="bg-background border border-border px-3 h-8 flex items-center justify-center rounded-lg shadow-sm">
              <span className="text-xs font-black text-foreground tabular-nums">{page}</span>
              <span className="text-[10px] text-muted-foreground font-bold mx-1.5 opacity-50">/</span>
              <span className="text-[10px] text-muted-foreground font-bold tabular-nums">{Math.ceil(total / 50)}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / 50) || loading}
              className="h-8 w-8 p-0 rounded-lg hover:bg-background"
              title="다음 페이지"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.ceil(total / 50))}
              disabled={page >= Math.ceil(total / 50) || loading}
              className="h-8 w-8 p-0 rounded-lg hover:bg-background border-muted-foreground/20"
              title="마지막 페이지"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
